<script setup>
import {
  defineProps,
  defineEmits,
  reactive,
  toRefs,
  ref,
  unref,
  watch,
  computed,
} from "vue";
import _ from "lodash";
import { format as formatDate, parseISO } from "date-fns";
import { watchDebounced } from "@vueuse/core";
import { useStore } from "vuex";
import { debounce } from "lodash";
import { useQuery } from "@tanstack/vue-query";
import Calendar from "primevue/calendar";
import { useToast } from "vue-toast-notification";
import { getCityCountryOptionLabel } from "@/components/program-manager/sessions/utils";
import { useGetCityOptions } from "@/components/program-manager/sessions/composable";
import { CANCEL, ERROR_TIMEOUT } from "@/constants";
import { columnsConfig } from "@/components/StudentFulfillment/components/modals/BookingFulfillmentModal/constants";
import DefaultFulfillmentModal from "@/components/StudentFulfillment/components/modals/DefaultFulfillmentModal";
import Magnifier from "@/components/shared/icons/Magnifier";
import CheckboxSelect from "@/components/shared/select/CheckboxSelect.vue";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import WarningModal from "@/components/StudentFulfillment/components/modals/BookingFulfillmentModal/Submodals/AssignmentWarning.vue";
import BlockRoomModal from "@/components/StudentFulfillment/components/modals/BookingFulfillmentModal/Submodals/BlockRoom.vue";
import ordersService from "@/services/orders";
import bookingService from "@/services/bookings";
import {
  areDatesIntervalConflicting,
  numberOfDaysBetweenDates,
} from "@/util/date";
import {
  BOOKING_TYPE,
  TOAST_MESSAGES,
} from "@/components/StudentFulfillment/components/report/TableauEventDetail/constants.js";

const emit = defineEmits(["closeModal"]);

const filterLabelMap = {
  cities: ({ city_ascii = "" }) => city_ascii,
  session_start_date: (date) => formatDate(date, "MM/dd/yyyy"),
  session_end_date: (date) => formatDate(date, "MM/dd/yyyy"),
  session_terms: (term) => term.name,
};

const props = defineProps({
  isModalOpen: {
    type: Boolean,
    default: false,
  },
  sessionStartDate: {
    type: String,
    default: undefined,
  },
  sessionEndDate: {
    type: String,
    default: undefined,
  },
  bedId: {
    type: String,
    default: undefined,
  },
  bookings: {
    type: Array,
    default: () => [],
  },
});

const store = useStore();
const toast = useToast();

const { isModalOpen, sessionStartDate, sessionEndDate, bedId } = toRefs(props);

const isWarningModalOpen = ref(false);
const isBlockModalOpen = ref(false);
const sessionDatesRange = ref([]);
const orderToAssign = ref({});

store.dispatch("programManager/fetchTerms");

const termOptions = computed(
  () => store.getters["programManager/getTermsData"]
);

const participantId = computed(
  () => store.getters?.getCurrentUser?.participantId
);

const pagination = reactive({
  limit: 10,
  page: 1,
  q: "",
});

const filters = reactive({
  text: undefined,
  extra: {
    session_name: undefined,
    session_terms: [],
    session_start_date: sessionStartDate.value ?? undefined,
    session_end_date: sessionEndDate.value ?? undefined,
    session_cities: [],
  },
});

const {
  isLoading: isLoadingOrders,
  data: ordersData,
  refetch: ordersRefetch,
} = useQuery({
  queryKey: ["fetchOrders"],
  queryFn: () =>
    ordersService.getOrdersLite({
      limit: pagination.limit,
      page: pagination.page,
      q: filters.text,
      extraParams: {
        session_name: filters.extra?.session_name ?? undefined,
        session_terms: filters.extra?.session_terms?.map((term) => term.id),
        session_arrival_date: filters.extra?.session_start_date
          ? formatDate(filters.extra.session_start_date, "yyyy-MM-dd")
          : undefined,
        session_departure_date: filters.extra?.session_end_date
          ? formatDate(filters.extra.session_end_date, "yyyy-MM-dd")
          : undefined,
        session_city_ids: filters.extra?.session_cities?.map((city) => city.id),
      },
    }),
});

const createNewBooking = async (bookingPayload) => {
  try {
    const createBookingBody = {
      participant_id: participantId.value,
      status_id: 6,
      bed_id: bedId.value,
      ...bookingPayload,
    };
    if (createBookingBody?.participant_id) {
      await bookingService.createBookings(createBookingBody);
      toast.open({
        message: TOAST_MESSAGES.successfully_created,
        type: "success",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
      emit("closeModal");
    }
  } catch (err) {
    toast.open({
      message: `${TOAST_MESSAGES.error_while_creating}: ${
        err?.response?.data?.message?.detail ?? ""
      }`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
};

const {
  execute: executeFetchCityOptions,
  isLoading: cityLoading,
  state: cityOptions,
} = useGetCityOptions({ immediate: false, throwError: true }, true);

const fetchCityOptions = debounce(async (search, loading) => {
  if (search && search?.length > 2) {
    loading(true);
    try {
      await executeFetchCityOptions(0, {
        city_ascii: search,
        cancel: true,
      });
      loading(false);
    } catch (e) {
      if (e?.message !== CANCEL) {
        loading(false);
      }
    }
  }
}, 0);

const closeModal = () => {
  orderToAssign.value = {};
  emit("closeModal", true);
  clearFilters();
};

const changePage = ({ limit, page }) => {
  pagination.limit = limit;
  pagination.page = page;
  unref(ordersRefetch)();
};

const handleSearch = (search) => {
  filters.text = search;
};

const clearFilters = () => {
  sessionDatesRange.value = [];
  filters.extra = {
    ...filters.extra,
    session_name: undefined,
    session_terms: [],
    session_start_date: sessionStartDate.value,
    session_end_date: sessionEndDate.value,
    session_cities: [],
  };
  pagination.page = 1;
};

watch(isModalOpen, (open) => {
  if (open) {
    executeFetchCityOptions(0, {
      city_ascii: "chi",
    });
    clearFilters();
  }
});

watch(
  () => sessionDatesRange.value,
  (newRange) => {
    if (newRange) {
      const [session_start_date, session_end_date] = newRange;
      if (session_start_date && session_end_date) {
        filters.extra = {
          ...filters.extra,
          session_start_date,
          session_end_date,
        };
      }
    } else {
      filters.extra = {
        ...filters.extra,
        session_start_date: undefined,
        session_end_date: undefined,
      };
    }
  }
);

watchDebounced(
  () => filters,
  () => {
    pagination.page = 1;
    unref(ordersRefetch)();
  },
  { immediate: false, debounce: 1000, deep: true }
);

const cancelRoomAssignment = () => {
  isWarningModalOpen.value = false;
};

const continueRoomAssignment = async () => {
  await createNewBooking(orderToAssign.value);
  isWarningModalOpen.value = false;
};

const cancelBlockRoom = () => {
  isBlockModalOpen.value = false;
};

const continueBlockRoom = async (blockData) => {
  isBlockModalOpen.value = false;
  await createNewBooking(blockData);
};

const dateRangesWithBookings = computed(
  () =>
    props.bookings?.map(({ booking_start_date, booking_end_date }) => ({
      booking_start_date,
      booking_end_date,
    })) ?? []
);

const checkConflicts = (start_date, end_date) => {
  return (
    dateRangesWithBookings.value?.reduce(
      (acc, curr) =>
        acc ||
        areDatesIntervalConflicting(
          new Date(parseISO(curr.booking_start_date)),
          new Date(parseISO(curr.booking_end_date)),
          start_date,
          end_date
        ),
      false
    ) ?? false
  );
};

const checkTwoDaysBuffer = (start_date, end_date) => {
  return (
    dateRangesWithBookings.value?.reduce((acc, curr) => {
      return (
        acc ||
        numberOfDaysBetweenDates(
          new Date(parseISO(curr.booking_end_date)),
          start_date
        ) <= 2 ||
        numberOfDaysBetweenDates(
          new Date(parseISO(curr.booking_start_date)),
          end_date
        ) <= 2
      );
    }, false) ?? false
  );
};

const openAssignModal = async () => {
  const { booking_start_date, booking_end_date } = orderToAssign.value;
  const isNewAssignmentDatesConflicting = checkConflicts(
    new Date(parseISO(booking_start_date)),
    new Date(parseISO(booking_end_date))
  );
  if (isNewAssignmentDatesConflicting) {
    toast.open({
      message: TOAST_MESSAGES.dates_conflicting_message,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    return;
  }

  if (
    checkTwoDaysBuffer(
      new Date(parseISO(booking_start_date)),
      new Date(parseISO(booking_end_date))
    )
  ) {
    isWarningModalOpen.value = true;
  } else {
    await continueRoomAssignment();
  }
};

const updateSelectedItems = (payload) => {
  const order = payload?.[0]?.order;

  if (order) {
    orderToAssign.value = {
      participant_id: order?.participant_id,
      booking_start_date: order?.session?.start_date,
      booking_end_date: order?.session?.end_date,
      participant_first_name: order?.participant_first_name,
      participant_last_name: order?.participant_last_name,
      booking_type: BOOKING_TYPE.BOOKING,
    };
  }
};
</script>

<template>
  <DefaultFulfillmentModal
    :is-modal-open="isModalOpen"
    :state="{
      loading: isLoadingOrders,
      ready: !isLoadingOrders,
    }"
    :data="{
      columns: columnsConfig,
      rows: ordersData?.data?.data?.items ?? [],
      count: ordersData?.data?.data?.count ?? 0,
    }"
    :filters="filters"
    :filter-label-map="filterLabelMap"
    :options="{
      allowUpdateHeaders: false,
      showRowId: true,
      updateSelectedOnCheck: true,
    }"
    :pagination="pagination"
    @close-modal="closeModal"
    @search-data="handleSearch"
    @clear-filters="clearFilters"
    @change-page="changePage"
    @update-selected-items="updateSelectedItems"
  >
    <template #header>
      <h1
        class="flex justify-center text-xl leading-tight text-center sm:text-2xl md:text-3xl"
      >
        Assign/Block room
      </h1>
    </template>
    <template #filters>
      <div
        class="flex w-full justify-between bg-blue-450 rounded-md ml-2 flex-wrap flex-0"
      >
        <BasicTextInput
          v-model="filters.extra.session_name"
          class="rounded-sm border-gray-300"
          outer-classes="w-60 mx-2 my-2"
          placeholder="Program Session"
          type="text"
        />
        <CheckboxSelect
          v-model="filters.extra.session_terms"
          :close-on-select="false"
          :components="{ OpenIndicator: Magnifier }"
          :deselect-from-dropdown="true"
          label="name"
          :multiple="true"
          option-key="name"
          :options="termOptions"
          outer-classes="w-60 mx-2 my-2 checkbox-placeholder"
          placeholder="Term"
          scroll-input="auto"
          input-max-height="29px"
        />
        <Calendar
          v-model="sessionDatesRange"
          selection-mode="range"
          class="w-60 mx-2 my-2 h-10 border-gray-300"
          input-class="border-gray-400 placeholder:text-gray-400"
          panel-class="bg-white border-gray-400"
          placeholder="Session Date Range"
        />
        <CheckboxSelect
          v-model="filters.extra.session_cities"
          :close-on-select="false"
          :components="{ OpenIndicator: Magnifier }"
          :deselect-from-dropdown="true"
          :filterable="false"
          label="city_ascii"
          :multiple="true"
          :get-option-label="getCityCountryOptionLabel"
          :options="cityOptions?.items || []"
          :loading="cityLoading"
          outer-classes="w-60 mx-2 my-2 checkbox-placeholder"
          placeholder="City"
          scroll-input="auto"
          input-max-height="29px"
          @search="fetchCityOptions"
        />
      </div>
    </template>

    <template #footer>
      <div class="flex align-center justify-center h-10 flex-0">
        <BaseButton outlined class="px-7" @click="closeModal">
          Cancel
        </BaseButton>

        <BaseButton
          :disabled="isLoadingOrders || _.isEmpty(orderToAssign)"
          class="w-48"
          @click="openAssignModal"
        >
          Assign Room
        </BaseButton>

        <BaseButton
          :disabled="isLoadingOrders"
          class="w-48"
          @click="isBlockModalOpen = true"
        >
          Block Room
        </BaseButton>
      </div>
    </template>
  </DefaultFulfillmentModal>
  <WarningModal
    :is-modal-open="isWarningModalOpen"
    @on-cancel="cancelRoomAssignment"
    @on-continue="continueRoomAssignment"
  />
  <BlockRoomModal
    :is-modal-open="isBlockModalOpen"
    :date-ranges-with-bookings="dateRangesWithBookings"
    @on-cancel="cancelBlockRoom"
    @on-continue="continueBlockRoom"
  />
</template>
